import { apiFetch } from './api';

const API_URL = import.meta.env.VITE_API_URL || '';

/* Webhook de entrada (docs/designsystem/propostas/webhook-de-entrada.html). Uma entrada por
   fonte — "Formulário do site", "Cenário do Make" — cada uma com a sua chave na URL. A URL é
   segredo: só o administrador a recebe, e nunca pela listagem. */

export interface WebhookEntradaConfig {
    /** "oportunidade" = contato + oportunidade na pipeline; "contato" = só o cadastro. */
    acao: 'oportunidade' | 'contato';
    /** "distribuir" = a mesma régua das conversas novas; "usuario" = sempre a mesma pessoa. */
    responsavel: 'distribuir' | 'usuario';
    usuario_id: number | null;
}

export interface WebhookEntrada {
    id: number;
    nome: string;
    status: number;
    criadaEm: string;
    entrada: WebhookEntradaConfig;
    recebidosHoje: number;
    oportunidadesHoje: number;
    ultimoEvento: string | null;
    /** A última chamada recusada (400): o que a pessoa olha quando "não chegou". */
    ultimaRecusa: { em: string; motivo: string } | null;
}

/** Um campo personalizado como vai no corpo: a chave e o que ele aceita. */
export interface WebhookEntradaCampo {
    chave: string;
    nome: string;
    /** 1 texto, 2 número, 3 data, 4 sim/não, 5 seleção. */
    tipo: number;
    opcoes: string[] | null;
}

interface Resposta<T> { sucesso: boolean; mensagem: string; dados?: T }

/* Lê o corpo como texto antes do JSON: rota inexistente na API responde 404 VAZIO. */
const chamar = async <T>(caminho: string, init?: RequestInit): Promise<Resposta<T>> => {
    try {
        const res = await apiFetch(`${API_URL}/api/webhookentrada/${caminho}`, init);
        const texto = await res.text();
        if (texto) {
            try {
                const json = JSON.parse(texto) as Resposta<T>;
                return { sucesso: res.ok && json.sucesso !== false, mensagem: json.mensagem ?? '', dados: json.dados };
            } catch { /* corpo que não é JSON: cai na mensagem por status */ }
        }
        if (res.status === 404) return { sucesso: false, mensagem: 'A API não tem este recurso. Ela pode estar desatualizada.' };
        if (res.status === 401) return { sucesso: false, mensagem: 'Sua sessão expirou. Entre de novo.' };
        if (res.status === 403) return { sucesso: false, mensagem: 'Só o administrador da empresa pode fazer isto.' };
        return { sucesso: res.ok, mensagem: res.ok ? '' : 'Não foi possível falar com o servidor.' };
    } catch {
        return { sucesso: false, mensagem: 'Erro de conexão com o servidor.' };
    }
};

const json = (method: string, corpo?: unknown): RequestInit => ({
    method,
    headers: { 'Content-Type': 'application/json' },
    body: corpo === undefined ? undefined : JSON.stringify(corpo),
});

export const WebhookEntradaService = {
    listar: () => chamar<WebhookEntrada[]>('Entradas'),

    obter: (id: number) => chamar<{ entrada: WebhookEntrada; campos: WebhookEntradaCampo[] }>(`Entradas/${id}`),

    /** Só o administrador. */
    endereco: (id: number) => chamar<{ url: string }>(`Entradas/${id}/Endereco`),

    criar: (nome: string) => chamar<{ entrada: WebhookEntrada; url: string }>('Entradas', json('POST', { nome })),

    salvar: (id: number, nome: string, entrada: WebhookEntradaConfig) =>
        chamar<WebhookEntrada>(`Entradas/${id}`, json('PUT', { nome, entrada })),

    /** A URL antiga para de funcionar na hora. */
    novaChave: (id: number) => chamar<{ url: string }>(`Entradas/${id}/NovaChave`, json('POST')),

    excluir: (id: number) => chamar<null>(`Entradas/${id}`, { method: 'DELETE' }),
};
